import React, { useState } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Space,
  Button,
  Table,
  Divider,
  Spin,
  Alert,
  Upload,
  message,
} from "antd";
import {
  ShoppingCartOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;
const ACCEPTED_EXTENSIONS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "doc",
  "docx",
  "xls",
  "xlsx",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const CreateRequisitionModal = ({
  visible,
  onCancel,
  onSubmit,
  departments,
  loading,
  activeBudget,
  lineItemsLoading,
  onFetchLineItems,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [itemForm] = Form.useForm();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (items.length === 0) {
        Modal.warning({
          title: "No Items",
          content: "Please add at least one item to the requisition.",
        });
        return;
      }

      const data = {
        ...values,
        budgetId: activeBudget?.budgetId,
        neededByDate: values.neededByDate?.format("YYYY-MM-DD"),
        items: items.map((item) => ({
          itemName: item.itemName,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          estimatedUnitPrice: item.estimatedUnitPrice,
        })),
      };

      const success = await onSubmit(
        data,
        fileList.map((file) => file.originFileObj || file),
      );
      if (success) {
        form.resetFields();
        setItems([]);
        setFileList([]);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleAddItem = async () => {
    try {
      const itemValues = await itemForm.validateFields();
      const newItem = {
        key: Date.now(),
        ...itemValues,
        totalPrice: itemValues.quantity * itemValues.estimatedUnitPrice,
      };
      setItems([...items, newItem]);
      itemForm.resetFields();
    } catch (error) {
      console.error("Item validation failed:", error);
    }
  };

  const handleRemoveItem = (key) => {
    setItems(items.filter((item) => item.key !== key));
  };

  const handleValuesChange = (changedValues) => {
    if ("departmentId" in changedValues) {
      // Reset line item selection when department changes
      form.setFieldsValue({ budgetLineItemId: undefined });
      if (onFetchLineItems) {
        onFetchLineItems(changedValues.departmentId);
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    itemForm.resetFields();
    setItems([]);
    setFileList([]);
    onCancel();
  };

  const beforeUpload = (file) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      message.error(`${file.name}: unsupported file type`);
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error(`${file.name}: file must be 10 MB or smaller`);
      return Upload.LIST_IGNORE;
    }
    if (fileList.length >= 5) {
      message.error("You can attach up to 5 files");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const totalAmount = items.reduce(
    (sum, item) => sum + (item.totalPrice || 0),
    0,
  );

  const hasLineItems =
    activeBudget && activeBudget.lineItems && activeBudget.lineItems.length > 0;

  const itemColumns = [
    {
      title: "Item Name",
      dataIndex: "itemName",
      key: "itemName",
      width: 150,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 60,
      align: "center",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 80,
    },
    {
      title: "Unit Price",
      dataIndex: "estimatedUnitPrice",
      key: "estimatedUnitPrice",
      width: 100,
      align: "right",
      render: (price) => parseFloat(price || 0).toLocaleString(),
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 100,
      align: "right",
      render: (price) => (
        <strong>{parseFloat(price || 0).toLocaleString()}</strong>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record.key)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Create New Requisition</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Create Requisition
        </Button>,
      ]}
      width={800}
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item
          name="departmentId"
          label="Item"
          rules={[{ required: true, message: "Please select an item" }]}
          extra="The active budget for this item will be automatically attached"
        >
          <Select placeholder="Select item" showSearch optionFilterProp="label">
            {departments.map((d) => (
              <Option
                key={d.departmentId}
                value={d.departmentId}
                label={`${d.name} ${d.code || ""} ${d.description || ""}`}
              >
                {d.name}
                {d.code ? ` (${d.code})` : ""}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Budget Line Item Section */}
        {lineItemsLoading && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <Spin size="small" tip="Loading budget line items..." />
          </div>
        )}

        {!lineItemsLoading && activeBudget && (
          <>
            {hasLineItems ? (
              <Form.Item
                name="budgetLineItemId"
                label="Budget Line Item"
                rules={[
                  {
                    required: true,
                    message: "Please select a budget line item",
                  },
                ]}
                extra="Select the budget line item this requisition will be charged to"
              >
                <Select
                  placeholder="Select budget line item"
                  showSearch
                  optionFilterProp="label"
                >
                  {activeBudget.lineItems.map((li) => {
                    const available = parseFloat(li.availableBalance || 0);
                    const isExhausted = available <= 0;
                    return (
                      <Option
                        key={li.lineItemId}
                        value={li.lineItemId}
                        disabled={isExhausted}
                        label={`${li.category || ""} ${li.description || ""}`}
                      >
                        {li.category} — Available: UGX{" "}
                        {available.toLocaleString()}
                        {isExhausted ? " (Exhausted)" : ""}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            ) : (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="The active budget has no line items with available balance. Please add line items to the budget first."
              />
            )}
          </>
        )}

        {!lineItemsLoading &&
          !activeBudget &&
          form.getFieldValue("departmentId") && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="No active budget found for this item. Please approve and activate a budget first."
            />
          )}

        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="Brief title for the requisition" />
        </Form.Item>

        <Form.Item
          name="justification"
          label="Business Justification"
          rules={[{ required: true, message: "Please provide justification" }]}
        >
          <TextArea rows={2} placeholder="Why is this requisition needed?" />
        </Form.Item>

        <Space style={{ width: "100%" }} size="large">
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true }]}
            initialValue="Medium"
          >
            <Select style={{ width: 120 }}>
              <Option value="Low">Low</Option>
              <Option value="Medium">Medium</Option>
              <Option value="High">High</Option>
              <Option value="Urgent">Urgent</Option>
            </Select>
          </Form.Item>

          <Form.Item name="neededByDate" label="Needed By Date">
            <DatePicker style={{ width: 150 }} />
          </Form.Item>
        </Space>

        <Form.Item name="description" label="Additional Notes">
          <TextArea rows={2} placeholder="Optional additional notes" />
        </Form.Item>
      </Form>

      <Form.Item
        label="Attachments"
        extra="Up to 5 files, 10 MB each. PDF, images, Word, and Excel files are accepted."
      >
        <Upload
          beforeUpload={beforeUpload}
          fileList={fileList}
          onChange={({ fileList: nextFiles }) =>
            setFileList(nextFiles.slice(0, 5))
          }
          onRemove={(file) => {
            setFileList((current) =>
              current.filter((item) => item.uid !== file.uid),
            );
            return true;
          }}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          multiple
          maxCount={5}
        >
          <Button icon={<UploadOutlined />} disabled={fileList.length >= 5}>
            Add attachments
          </Button>
        </Upload>
      </Form.Item>

      <Divider>Requisition Items</Divider>

      {/* Add Item Form */}
      <Form form={itemForm} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item
          name="itemName"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="Item name" style={{ width: 150 }} />
        </Form.Item>
        <Form.Item
          name="quantity"
          rules={[{ required: true, message: "Required" }]}
          initialValue={1}
        >
          <InputNumber placeholder="Qty" min={1} style={{ width: 70 }} />
        </Form.Item>
        <Form.Item name="unit" initialValue="pieces">
          <Input placeholder="Unit" style={{ width: 80 }} />
        </Form.Item>
        <Form.Item
          name="estimatedUnitPrice"
          rules={[{ required: true, message: "Required" }]}
        >
          <InputNumber
            placeholder="Unit price"
            min={0}
            style={{ width: 120 }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/,/g, "")}
          />
        </Form.Item>
        <Form.Item name="description">
          <Input placeholder="Description" style={{ width: 150 }} />
        </Form.Item>
        <Form.Item>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddItem}>
            Add
          </Button>
        </Form.Item>
      </Form>

      {/* Items Table */}
      <Table
        columns={itemColumns}
        dataSource={items}
        size="small"
        pagination={false}
        scroll={{ y: 200 }}
        footer={() => (
          <div style={{ textAlign: "right" }}>
            <strong>Total: UGX {totalAmount.toLocaleString()}</strong>
          </div>
        )}
      />
    </Modal>
  );
};

export default CreateRequisitionModal;
